import { Box } from "@portex-pro/ui-components";
import { useUpdateShipperQuoteRequestMutation } from "api/rest/quote-requests";
import { useCreateQuoteRequestMutation } from "api/rest/quote-requests/createQuoteRequest";
import { usePaginatedGetManyShipperQuoteRequestsQuery } from "api/rest/quote-requests/getManyShipperQuoteRequests";
import Text from "components/Text";
import ThrottledTextInput from "components/ThrottledTextInput";
import withAsync from "components/withAsync";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Sentry } from "sentry";

import { useQuoteRequestSelector } from "../_store";
import { quoteRequestActions } from "../_store/quoteRequestSliceV2";
import TemplatesTableView from "./TemplateTableView";

export const RecentTemplates = withAsync({
  useHook: () => {
    const { selectedMode, templateSearch } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);

    return usePaginatedGetManyShipperQuoteRequestsQuery({
      queryParams: { search: templateSearch, mode: [selectedMode], status: ["TEMPLATE"] },
    });
  },
  Component: ({
    loadedData: {
      data: {
        data: { quoteRequests: templates },
      },
      paginationOptions,
    },
  }) => {
    const { t } = useTranslation(["common"]);
    const { enqueueSnackbar } = useSnackbar();
    const templateSearch = useQuoteRequestSelector((store) => store.quoteRequestSliceV2.templateSearch);
    const { quoteRequest: airQuoteRequest, stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
    const mode = useQuoteRequestSelector((store) => store.quoteRequestSliceV2.selectedMode);
    const setTemplateSearch = quoteRequestActions.useSetTemplateSearch();
    const [createQuoteRequest] = useCreateQuoteRequestMutation();
    const [updateQuoteRequest] = useUpdateShipperQuoteRequestMutation();

    const onClick = async (quoteRequestId: number) => {
      try {
        await createQuoteRequest({ body: { quoteRequestId, mode } }).unwrap();
      } catch (e) {
        Sentry.captureException(e);
        enqueueSnackbar(t("common:errors.generic"), { variant: "error" });
      }
    };

    const onDelete = async (quoteRequestId: number) => {
      try {
        await updateQuoteRequest({
          urlParams: { quoteRequestId },
          body: {
            template_deleted: true,
          },
        }).unwrap();
      } catch (e) {
        Sentry.captureException(e);
        enqueueSnackbar(t("common:errors.generic"), { variant: "error" });
      }
    };

    if (stepIndex > 0 || !!airQuoteRequest.id) {
      return null;
    }

    return (
      <Box display="flex" flexDirection="column" gridRowGap="24px" width="100%" marginTop="32px">
        <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
          <Text size="large" weight="bold">
            Recent Templates
          </Text>
          <ThrottledTextInput
            margin="none"
            fullWidth={false}
            placeholder="Search templates"
            search
            value={templateSearch ?? ""}
            onChange={setTemplateSearch}
            InputProps={{ style: { background: "white" } }}
          />
        </Box>

        {!templates || templates.length === 0 ? (
          <div className="w-full flex items-center justify-center min-h-[500px] h-full border bg-white rounded-[8px]">
            <Text size="x-large" weight="bold">
              No Templates
            </Text>
          </div>
        ) : (
          <TemplatesTableView
            items={templates}
            onClickTemplate={onClick}
            onDeleteTemplate={onDelete}
            paginationOptions={paginationOptions}
          />
        )}
      </Box>
    );
  },
});
